import { useState, useEffect } from "react";
import { getInsightsFromAggregateResult } from "../../insights/utils";
import { useGetAggregateAssets } from "@hooks/use-get/aggregate-assets";
import type { AggregateAssetResult } from "@hooks/use-get/aggregate-assets";
import { VisualizationCardContent } from "../../../components/visualization-card/card-content";
import { OutlinedSelect } from "../../../components/outlined-select";
import { Box } from "@mui/material";
import { LoadingTableBlock } from "../../../components/data-table/loading-table-block";
import { getLocalStorageItem, setLocalStorageItem, removeLocalStorageItem } from "../../../utils";
import { VisualizationCardType } from "../../../components/visualization-card/types";
import { WpAsset } from "@hooks/use-get/assets/types";
import { useAssetsConfigContext } from "@hooks/use-config-context/asset";
import { useTicketConfigContext } from "@hooks/use-config-context/ticket";
import { useUserConfigContext } from "@hooks/use-config-context/user";
import { isFieldVisualization } from "@hooks/use-config/utils";
import { ASSET_GOOGLE_SYNC_COLUMN_IDS } from "@hooks/use-config/asset/utils";
import { TICKET_ASSET_DERIVED_COLUMN_IDS } from "@hooks/use-config/ticket/utils";
import { WpTicketMetaBox } from "@hooks/use-get/tickets/types"
import { SiteUser } from "@hooks/use-get/users/types";

/**
 * Fetches server-aggregated {value, count} rows for one groupBy column at a
 * time (over the FULL dataset, not just one fetched page) and re-fetches
 * whenever the selected column changes.
 */
const useAggregateInsight = (
  column: string | null,
  postType: "asset" | "ticket" | "user"
) => {
  const { getAggregateAssets } = useGetAggregateAssets();
  const [data, setData] = useState<AggregateAssetResult[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!column) {
      setData([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getAggregateAssets({ groupBy: [column], postType })
      .then((result) => {
        if (!cancelled) setData(result.data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [column, postType, getAggregateAssets]);

  return { data, loading };
};

export const AssetChartWidget = ({ id }: { id: string | number }) => {
  const initialColumn = getLocalStorageItem(`chart-widget-${id}`);
  const [column, setColumn] = useState<keyof WpAsset | null>(
    initialColumn ?? null
  );

  const {
    tableColumns,
    config: assetsConfig,
    loading: configLoading,
  } = useAssetsConfigContext();

  const { data: aggregateData, loading: aggregateLoading } = useAggregateInsight(
    column as string | null,
    "asset"
  );

  const insightData = column
    ? getInsightsFromAggregateResult(column, aggregateData, assetsConfig[column])
    : null;
  // Google Sync fields (device id/type/status/last-sync, OS version) are
  // excluded from the chart picker entirely, not just default-hidden.
  // Number-type fields (e.g. "cost") are near-unique per asset — grouping/
  // counting by exact value produces no meaningful chart (same reason
  // Reporting only ever uses "cost" as a sum/average dimension, never as a
  // group-by category, see reports.config.ts).
  const columnOptions = tableColumns
    .filter((col) => isFieldVisualization(col.inputType))
    .filter((col) => !(ASSET_GOOGLE_SYNC_COLUMN_IDS as readonly string[]).includes(col.id))
    .filter((col) => assetsConfig[col.id]?.type !== "number")
    .map((col) => ({
      label: col.label,
      value: col.id,
    }));

  // A previously-persisted selection may point at a column that's since been
  // excluded (or removed from config) — clear it rather than requesting an
  // invalid column and showing an empty chart with no valid selected option.
  useEffect(() => {
    if (configLoading || !column) return;
    if (!columnOptions.some((opt) => opt.value === column)) {
      setColumn(null);
      removeLocalStorageItem(`chart-widget-${id}`);
    }
  }, [configLoading, columnOptions, column, id]);

  const handleSetColumn = (value: keyof WpAsset) => {
    setColumn(value);
    setLocalStorageItem(`chart-widget-${id}`, value);
  };

  const handleTypeChange = (type: VisualizationCardType) => {
    setLocalStorageItem(`chart-widget-type-${id}`, type);
  };

  if (configLoading || aggregateLoading) {
    return <LoadingTableBlock sx={{ mt: 0 }} />;
  }

  return (
    <Box px={1} pt={2}>
      <Box sx={{ mx: "auto" }}>
        <OutlinedSelect
          label="Select Data Column"
          value={column}
          options={columnOptions}
          onChange={(value) => handleSetColumn(value as keyof WpAsset)}
          sx={{ minWidth: "100%", mb: 2 }}
        />
      </Box>
      {!!insightData && (
        <VisualizationCardContent
          data={insightData}
          type={getLocalStorageItem(`chart-widget-type-${id}`) ?? undefined}
          onTypeChange={handleTypeChange}
        />
      )}
    </Box>
  );
};

export const TicketChartWidget = ({ id }: { id: string | number }) => {
  const initialColumn = getLocalStorageItem(`chart-widget-${id}`);
  const [column, setColumn] = useState<keyof WpTicketMetaBox | null>(
    initialColumn ?? null
  );

  const {
    tableColumns,
    config: ticketsConfig,
    loading: configLoading,
  } = useTicketConfigContext();

  const { data: aggregateData, loading: aggregateLoading } = useAggregateInsight(
    column as string | null,
    "ticket"
  );

  const insightData = column
    ? getInsightsFromAggregateResult(column, aggregateData)
    : null;
  // "Model"/"Serial Number"/etc. are read off the linked Asset, not real
  // ticket columns, so the backend can't aggregate them here — Reporting
  // (which fetches the asset relationship directly) covers these instead.
  // Number-type fields are near-unique per ticket — grouping/counting by
  // exact value produces no meaningful chart (see AssetChartWidget above).
  const columnOptions = tableColumns
    .filter((col) => isFieldVisualization(col.inputType))
    .filter((col) => !(TICKET_ASSET_DERIVED_COLUMN_IDS as readonly string[]).includes(col.id))
    .filter((col) => ticketsConfig[col.id]?.type !== "number")
    .map((col) => ({
      label: col.label,
      value: col.id,
    }));

  // A previously-persisted selection may point at a column that's since been
  // excluded (or removed from config) — clear it rather than requesting an
  // invalid column and showing an empty chart with no valid selected option.
  useEffect(() => {
    if (configLoading || !column) return;
    if (!columnOptions.some((opt) => opt.value === column)) {
      setColumn(null);
      removeLocalStorageItem(`chart-widget-${id}`);
    }
  }, [configLoading, columnOptions, column, id]);

  const handleSetColumn = (value: string) => {
    setColumn(value as keyof WpTicketMetaBox);
    setLocalStorageItem(`chart-widget-${id}`, value);
  };

  const handleTypeChange = (type: VisualizationCardType) => {
    setLocalStorageItem(`chart-widget-type-${id}`, type);
  };

  if (configLoading || aggregateLoading) {
    return <LoadingTableBlock sx={{ mt: 0 }} />;
  }

  return (
    <Box px={1} pt={2}>
      <Box sx={{ mx: "auto" }}>
        <OutlinedSelect
          label="Select Data Column"
          value={column}
          options={columnOptions}
          onChange={(value) => handleSetColumn(value as string)}
          sx={{ minWidth: "100%", mb: 2 }}
        />
      </Box>
      {!!insightData && (
        <VisualizationCardContent
          data={insightData}
          type={getLocalStorageItem(`chart-widget-type-${id}`) ?? undefined}
          onTypeChange={handleTypeChange}
        />
      )}
    </Box>
  );
};

export const UserChartWidget = ({ id }: { id: string | number }) => {
  const initialColumn = getLocalStorageItem(`chart-widget-${id}`);
  const [column, setColumn] = useState<keyof SiteUser | null>(
    initialColumn ?? null
  );

  const {
    tableColumns,
    config: usersConfig,
    loading: configLoading,
  } = useUserConfigContext();

  const { data: aggregateData, loading: aggregateLoading } = useAggregateInsight(
    column as string | null,
    "user"
  );

  const insightData = column
    ? getInsightsFromAggregateResult(column, aggregateData, usersConfig[column])
    : null;

  // Number-type fields are near-unique per user — grouping/counting by
  // exact value produces no meaningful chart (see AssetChartWidget above).
  const columnOptions = tableColumns
    .filter((col) => isFieldVisualization(col.inputType))
    .filter((col) => usersConfig[col.id]?.type !== "number")
    .map((col) => ({
      label: col.label,
      value: col.id,
    }));

  const handleSetColumn = (value: string) => {
    setColumn(value as keyof SiteUser);
    setLocalStorageItem(`chart-widget-${id}`, value);
  };

  const handleTypeChange = (type: VisualizationCardType) => {
    setLocalStorageItem(`chart-widget-type-${id}`, type);
  };

  if (configLoading || aggregateLoading) {
    return <LoadingTableBlock sx={{ mt: 0 }} />;
  }

  return (
    <Box px={1} pt={2}>
      <Box sx={{ mx: "auto" }}>
        <OutlinedSelect
          label="Select Data Column"
          value={column}
          options={columnOptions}
          onChange={(value) => handleSetColumn(value as string)}
          sx={{ minWidth: "100%", mb: 2 }}
        />
      </Box>
      {!!insightData && (
        <VisualizationCardContent
          data={insightData}
          type={getLocalStorageItem(`chart-widget-type-${id}`) ?? undefined}
          onTypeChange={handleTypeChange}
        />
      )}
    </Box>
  );
};



export default AssetChartWidget;
